/*
 * SARSignatureModal — lienzo HTML5 canvas para dibujar la rúbrica del técnico.
 * Soporta ratón y touch (móvil). Al guardar, envía { name, signature_b64 } al backend.
 */
import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import api from '../../api/client';
import { X, Loader2, Eraser, PenLine } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const SARSignatureModal = ({ sarId, initialName = '', onClose, onSigned }) => {
  const { user } = useAuth();
  const canvasRef = useRef(null);
  const [name, setName] = useState(initialName || user?.name || '');
  const [drawing, setDrawing] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = '#111';
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  }, []);

  const getPoint = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const pt = e.touches ? e.touches[0] : e;
    return { x: (pt.clientX - rect.left) * scaleX, y: (pt.clientY - rect.top) * scaleY };
  };

  const start = (e) => {
    e.preventDefault();
    setDrawing(true);
    const { x, y } = getPoint(e);
    const ctx = canvasRef.current.getContext('2d');
    ctx.beginPath();
    ctx.moveTo(x, y);
  };
  const move = (e) => {
    if (!drawing) return;
    e.preventDefault();
    const { x, y } = getPoint(e);
    const ctx = canvasRef.current.getContext('2d');
    ctx.lineTo(x, y);
    ctx.stroke();
    setDirty(true);
  };
  const end = () => setDrawing(false);

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setDirty(false);
  };

  const save = async () => {
    if (!name.trim()) { toast.error('Escribe el nombre del técnico'); return; }
    if (!dirty) { toast.error('Traza tu firma en el recuadro'); return; }
    setSaving(true);
    try {
      const canvas = canvasRef.current;
      const dataUrl = canvas.toDataURL('image/png');
      const { data } = await api.post(`/research/sar/${sarId}/signature`, {
        name: name.trim(),
        signature_b64: dataUrl,
      });
      toast.success('SAR firmado');
      if (onSigned) onSigned(data);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error firmando');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[10002] bg-black/85 backdrop-blur flex items-center justify-center p-4"
      onClick={onClose} data-testid="sar-signature-modal">
      <div onClick={(e) => e.stopPropagation()}
        className="bg-[#0a0a0a] border border-white/15 rounded-3xl w-full max-w-lg flex flex-col">
        <div className="p-5 border-b border-white/10 flex items-start justify-between gap-3">
          <div>
            <div className="text-[10px] font-black tracking-widest text-[#A3FF12]">FIRMAR SAR</div>
            <h2 className="text-lg font-black text-white mt-1">Cerrar informe en visita</h2>
            <p className="text-white/50 text-xs mt-1">La firma se embeberá en el PDF del SAR.</p>
          </div>
          <button onClick={onClose} className="text-white/40 hover:text-white p-1" data-testid="close-signature-modal"><X size={22}/></button>
        </div>

        <div className="p-5 space-y-4">
          <label className="block">
            <span className="text-[10px] uppercase text-white/50 font-bold">Nombre del técnico</span>
            <input value={name} onChange={(e) => setName(e.target.value)}
              data-testid="signature-name-input"
              placeholder="Nombre y apellidos"
              className="mt-1 w-full bg-black border border-white/10 rounded px-3 py-2 text-sm text-white outline-none focus:border-[#A3FF12]/50"/>
          </label>

          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] uppercase text-white/50 font-bold flex items-center gap-1">
                <PenLine size={12}/> Rúbrica
              </span>
              <button onClick={clear} data-testid="signature-clear"
                className="text-[10px] font-bold text-white/60 hover:text-white inline-flex items-center gap-1">
                <Eraser size={11}/> Borrar
              </button>
            </div>
            <canvas ref={canvasRef}
              data-testid="signature-canvas"
              width={600} height={200}
              className="w-full bg-white rounded-lg border-2 border-dashed border-white/20 touch-none"
              style={{ aspectRatio: '3 / 1' }}
              onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
              onTouchStart={start} onTouchMove={move} onTouchEnd={end}/>
            <p className="text-white/40 text-[10px] mt-1">Firma con el dedo (móvil/tablet) o el ratón.</p>
          </div>

          <button onClick={save} disabled={saving}
            data-testid="signature-save"
            className="w-full bg-[#A3FF12] text-black font-black py-3 rounded-xl text-sm hover:bg-[#b8ff3a] disabled:opacity-40 flex items-center justify-center gap-2">
            {saving ? <><Loader2 className="animate-spin" size={14}/> Guardando…</> : 'Guardar firma'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SARSignatureModal;
